import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Trash2, Sparkles, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const quickActions = [
  { label: "Check Rooms", message: "What rooms are available and what are the prices?" },
  { label: "Plan My Trip", message: "I'd like help planning my trip to Koraput. What activities do you recommend?" },
  { label: "Direct Booking Benefits", message: "What are the benefits of booking directly with the hotel?" },
  { label: "Nearby Attractions", message: "What are the must-visit places near the hotel?" },
];

// Format markdown-style text to JSX
function formatMessage(text) {
  if (!text) return null;
  
  // Split by double newlines for paragraphs
  const paragraphs = text.split(/\n\n+/);
  
  return paragraphs.map((paragraph, pIndex) => {
    // Split by single newlines within paragraph
    const lines = paragraph.split(/\n/);
    
    return (
      <p key={pIndex} className={pIndex > 0 ? 'mt-3' : ''}>
        {lines.map((line, lIndex) => {
          // Process bold text (**text**)
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          
          return (
            <span key={lIndex}>
              {lIndex > 0 && <br />}
              {parts.map((part, partIndex) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  // Bold text
                  return (
                    <strong key={partIndex} className="font-semibold">
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                return <span key={partIndex}>{part}</span>;
              })}
            </span>
          );
        })}
      </p>
    );
  });
}

export function EmeraldAI({ fullPage = false, isModal = false }) {
  const [isOpen, setIsOpen] = useState(fullPage || isModal);
  const [contact, setContact] = useState({ 
    phones: ['+91 6372 350 065'], 
    email: 'thehotelemerald@gmail.com' 
  });
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Namaste! I'm **Emerald AI**, your personal guide to The Hotel Emerald and the beautiful Koraput region. How may I assist you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    // Scroll within the container, not the whole page
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Fetch contact info from site settings
  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/site-settings`);
        if (response.data?.contact) {
          setContact(prev => ({ ...prev, ...response.data.contact }));
        }
      } catch (error) {
        console.warn('Failed to fetch contact info for chat:', error);
      }
    };
    fetchContact();
  }, []);

  useEffect(() => {
    // Small delay to ensure DOM is updated before scrolling
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  useEffect(() => {
    if ((isOpen || isModal) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isModal]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/chat`, {
        message: messageText,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      const phone = contact.phones?.[0] || '+91 6372 350 065';
      const email = contact.email || 'thehotelemerald@gmail.com';
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `I apologize, but I'm having trouble connecting right now. Please try again or contact us directly at ${phone} or ${email}.`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API_URL}/api/chat/${sessionId}`);
      } catch (error) {
        console.error('Failed to clear chat:', error);
      }
    }
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: "Namaste! I'm **Emerald AI**, your personal guide to The Hotel Emerald and the beautiful Koraput region. How may I assist you today?"
    }]);
  };

  // For modal mode - minimal wrapper
  if (isModal) {
    return (
      <div className="h-full flex flex-col" data-testid="emerald-ai-modal-content">
        {/* Messages */}
        <div 
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/10"
        >
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                  msg.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-sm'
                    : 'bg-card border border-border text-foreground rounded-bl-sm shadow-sm'
                }`}
              >
                {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-card border border-border px-4 py-2.5 rounded-2xl rounded-bl-sm shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  <span className="text-xs text-muted-foreground">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions - show only initially */}
        {messages.length <= 2 && (
          <div className="px-4 py-2 border-t border-border bg-background">
            <div className="flex flex-wrap gap-1.5">
              {quickActions.slice(0, 3).map((action, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(action.message)}
                  disabled={isLoading}
                  className="text-xs px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors disabled:opacity-50"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-3 border-t border-border bg-background">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything..."
              className="flex-1 px-4 py-2.5 text-sm bg-muted border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
              disabled={isLoading}
              data-testid="modal-chat-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={isLoading || !input.trim()}
              className="h-10 w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
              data-testid="modal-chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // For full page mode, render without the floating button wrapper
  if (fullPage) {
    return (
      <div className="h-full flex flex-col bg-card border border-border rounded-2xl overflow-hidden" data-testid="emerald-ai-fullpage">
        {/* Header */}
        <div className="bg-emerald-800 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-serif text-xl">Emerald AI</h2>
              <p className="text-sm text-emerald-200">Your Personal Koraput Guide</p>
            </div>
          </div>
          <button
            onClick={clearChat}
            className="p-2 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-2 text-sm"
            data-testid="clear-chat-btn-full"
          >
            <Trash2 className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Messages */}
        <div 
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-6 space-y-4 bg-muted/20"
        >
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] px-5 py-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : 'bg-card border border-border text-foreground rounded-bl-md shadow-sm'
                }`}
                data-testid={`chat-message-full-${index}`}
              >
                {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-card border border-border px-5 py-3 rounded-2xl rounded-bl-md shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                  <span className="text-sm text-muted-foreground">Emerald AI is thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {messages.length <= 2 && (
          <div className="px-6 py-3 border-t border-border bg-card">
            <p className="text-xs text-muted-foreground mb-2">Quick actions:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(action.message)}
                  disabled={isLoading}
                  className="text-sm px-4 py-2 bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors disabled:opacity-50"
                  data-testid={`quick-action-full-${index}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-border bg-card">
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about rooms, plan your trip, explore Koraput..."
              className="flex-1 px-5 py-3 bg-muted border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
              disabled={isLoading}
              data-testid="chat-input-full"
            />
            <Button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-12 w-12"
              data-testid="chat-send-btn-full"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center ${isOpen ? 'hidden' : ''}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-toggle-btn"
      >
        <Sparkles className="w-6 h-6" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[550px] max-h-[calc(100vh-100px)] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-emerald-800 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-serif text-sm">Emerald AI</h3>
                  <p className="text-xs text-emerald-200">Your Personal Guide</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Link
                  to="/emerald-ai"
                  className="p-2 hover:bg-emerald-700 rounded-lg transition-colors"
                  title="Open full experience"
                  data-testid="expand-chat-btn"
                >
                  <Maximize2 className="w-4 h-4" />
                </Link>
                <button
                  onClick={clearChat}
                  className="p-2 hover:bg-emerald-700 rounded-lg transition-colors"
                  title="Clear chat"
                  data-testid="clear-chat-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-emerald-700 rounded-lg transition-colors"
                  data-testid="close-chat-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-emerald-600 text-white rounded-br-md'
                        : 'bg-card border border-border text-foreground rounded-bl-md'
                    }`}
                    data-testid={`chat-message-${index}`}
                  >
                    {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-card border border-border px-4 py-3 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length <= 2 && (
              <div className="px-4 py-2 border-t border-border bg-card">
                <p className="text-xs text-muted-foreground mb-2">Quick actions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => sendMessage(action.message)}
                      disabled={isLoading}
                      className="text-xs px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors disabled:opacity-50"
                      data-testid={`quick-action-${index}`}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-3 border-t border-border bg-card">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about rooms, attractions..."
                  className="flex-1 px-4 py-2.5 text-sm bg-muted border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-10 w-10"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
